import React from "react";
import SearchIcon from "@mui/icons-material/Search";
import styles from "./massengermodal.module.css";
import LeftSectionChatItemModule, {
  LeftSectionChatItem as LeftSectionChatItemNamed,
} from "./LeftSectionChatItem";
import useLeftSection from "../../../hooks/messenger/useLeftSection";
import {
  getCanonicalEntityIds,
  isSameEntity,
} from "../../../utils/messengerLeftSection";

const resolveComponent = (mod, named) => {
  if (typeof mod === "function") return mod;
  if (mod && typeof mod.default === "function") return mod.default;
  if (typeof named === "function") return named;
  if (named && typeof named.default === "function") return named.default;
  return () => null;
};

const LeftSectionChatItem = resolveComponent(
  LeftSectionChatItemModule,
  LeftSectionChatItemNamed,
);

const LeftSection = ({
  searchInputRef,
  handleSearch,
  searchTerm,
  searchedData,
  connectedShops = [],
  connectedProfilesOnly = false,
  selectedConnectedTabId = "",
  selectedConnectedTabLabel = "",
  userShops = [],
  activeTabType = "user",
  activeTabLabel = "User Profile",
  selectedProfile = null,
  onSelectShop,
  handleSearchDataClick,
  safeGetAsset = (src) => src || "",
  getAvatarUrl = (entity) =>
    entity?.profilePic || entity?.imageUrl || entity?.image || "",
  users,
  chatHistory = {},
  isLoading = false,
  chatsError = "",
  onRetryChats,
  connectionsLoading = false,
  connectionsError = "",
  connectionsSourceCount = 0,
  onRetryConnections,
  selectedUser,
  handleUserClick,
  safeRender = (val) =>
    typeof val === "string" ? val : val ? String(val) : "",
}) => {
  const {
    displayUsers,
    filteredUsers: profileFilteredUsers,
    handleListItemClick,
    handleOpenChatForProfile,
    selectedConnectedProfileIds,
  } = useLeftSection({
    searchTerm,
    users,
    searchedData,
    safeRender,
    connectedShops,
    connectedProfilesOnly,
    selectedConnectedTabId,
    userShops,
    activeTabType,
    selectedProfile,
    onSelectShop,
    handleUserClick,
    handleSearchDataClick,
  });

  const isSearchMode = Boolean(searchTerm);
  const headingText = isSearchMode
    ? "Search Results"
    : connectedProfilesOnly
      ? `${selectedConnectedTabLabel || activeTabLabel || "Category"} Connections`
      : activeTabType !== "user"
        ? `${activeTabLabel || "Shop"} Chats & Connections`
        : profileFilteredUsers.length > 0
          ? "Recent Chats"
          : "All Connections";

  return (
    <div className={styles.leftSection}>
      <div className={styles.searchContainer}>
        <SearchIcon className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search shops or contacts..."
          ref={searchInputRef}
          onChange={handleSearch}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.chatList} aria-label={headingText} role="list">
        {isLoading ? (
          <div
            className={styles.chatSkeletonList}
            aria-label="Loading conversations"
            aria-busy="true"
          >
            {Array.from({ length: 7 }, (_, index) => (
              <div
                className={styles.chatSkeletonRow}
                key={`chat-skeleton-${index}`}
              >
                <span className={styles.skeletonAvatar} />
                <span className={styles.skeletonText}>
                  <span className={styles.skeletonName} />
                  <span className={styles.skeletonSubtitle} />
                </span>
              </div>
            ))}
          </div>
        ) : displayUsers && displayUsers.length > 0 ? (
          displayUsers.map((user, index) => {
            const isOwnShop = userShops.some((shop) =>
              isSameEntity(shop, user),
            );
            const isChatItem = Boolean(user?.otherParticipantData);
            const contact = isChatItem ? user.otherParticipantData : user;
            const itemId =
              getCanonicalEntityIds(user)[0] ||
              String(
                user?._id ||
                  user?.id ||
                  user?.shopid ||
                  user?.userid ||
                  `user-item-${index}`,
              );
            const nameCandidates = [
              contact?.shopname,
              contact?.displayName,
              contact?.fullname,
              contact?.name,
              user?.shopname,
              user?.displayName,
              user?.fullname,
              user?.name,
            ];
            const rawName =
              nameCandidates.find(
                (n) =>
                  n &&
                  typeof n === "string" &&
                  n.trim() !== "" &&
                  n.trim().toUpperCase() !== "N/A",
              ) ||
              (contact?.businessType &&
              contact.businessType.toUpperCase() !== "N/A"
                ? contact.businessType
                : user?.businessType &&
                    user.businessType.toUpperCase() !== "N/A"
                  ? user.businessType
                  : "Connected Profile");
            const nameText = safeRender(rawName) || "Connected Profile";

            const chatMessages = Array.isArray(chatHistory?.[String(user?._id)])
              ? chatHistory[String(user?._id)]
              : [];
            const latestChatMessage = chatMessages.reduce((latest, message) => {
              if (!latest) return message;
              const latestTime = new Date(
                latest.timestamp || latest.createdAt || 0,
              ).getTime();
              const messageTime = new Date(
                message.timestamp || message.createdAt || 0,
              ).getTime();
              return messageTime >= latestTime ? message : latest;
            }, null);

            let rawStatus = "";
            if (isChatItem) {
              if (latestChatMessage?.isDeleted) {
                rawStatus = "This message was deleted";
              } else if (
                latestChatMessage?.content &&
                typeof latestChatMessage.content === "string" &&
                latestChatMessage.content.trim() !== "" &&
                latestChatMessage.content.trim().toUpperCase() !== "N/A"
              ) {
                rawStatus = latestChatMessage.content;
              } else {
                const chatMsg = user?.message || user?.messsage;
                if (
                  chatMsg &&
                  typeof chatMsg === "string" &&
                  chatMsg.trim() !== "" &&
                  chatMsg.trim().toUpperCase() !== "N/A"
                ) {
                  rawStatus = chatMsg;
                } else {
                  rawStatus = "";
                }
              }
            } else {
              const statusCandidates = isOwnShop
                ? [
                    user?.businessType &&
                    user.businessType.toUpperCase() !== "N/A"
                      ? `${user.businessType} (My Shop)`
                      : "My Shop",
                  ]
                : [user?.businessType, user?.position, user?.status];
              const validStatus = statusCandidates.find(
                (s) =>
                  s &&
                  typeof s === "string" &&
                  s.trim() !== "" &&
                  s.trim().toUpperCase() !== "N/A",
              );
              rawStatus = validStatus || "";
            }
            const statusText = safeRender(rawStatus);
            const avatarSrc = isChatItem
              ? safeGetAsset(getAvatarUrl(contact))
              : safeGetAsset(getAvatarUrl(user));

            // Item is selected if its chat conversation is currently open in CenterSection
            const targetId = String(
              contact?._id ||
                contact?.shopid ||
                contact?.userid ||
                user?.shopid ||
                user?.id ||
                user?._id ||
                "",
            );
            const selectedOtherId = String(
              selectedUser?.otherParticipantData?._id ||
                selectedUser?.otherParticipantData?.shopid ||
                selectedUser?.otherParticipantData?.userid ||
                selectedUser?.shopid ||
                selectedUser?.id ||
                selectedUser?._id ||
                "",
            );
            const isSelectedConnectedProfile = selectedConnectedProfileIds.some(
              (id) => getCanonicalEntityIds(user).includes(id),
            );

            // After a page refresh selectedUser is null; fall back to matching
            // selectedProfile (which is restored from localStorage by the parent)
            // so the correct item stays highlighted in the left panel.
            const isMatchingRestoredProfile =
              !selectedUser &&
              selectedProfile &&
              isSameEntity(user, selectedProfile);

            const isSelected =
              Boolean(
                selectedUser?._id &&
                String(selectedUser._id) === String(user?._id),
              ) ||
              Boolean(
                selectedOtherId && targetId && selectedOtherId === targetId,
              ) ||
              Boolean(
                selectedUser?.otherParticipantData &&
                (isSameEntity(contact, selectedUser.otherParticipantData) ||
                  isSameEntity(user, selectedUser.otherParticipantData)),
              ) ||
              Boolean(isMatchingRestoredProfile || isSelectedConnectedProfile);

            const displayUnreadCount = isSelected
              ? 0
              : Number(user?.unreadCount ?? contact?.unreadCount ?? 0);

            return (
              <LeftSectionChatItem
                key={itemId}
                item={user}
                isSelected={isSelected}
                isOwnShop={isOwnShop}
                nameText={nameText}
                statusText={statusText}
                avatarSrc={avatarSrc}
                unreadCount={displayUnreadCount}
                handleListItemClick={handleListItemClick}
                handleOpenChatForProfile={handleOpenChatForProfile}
              />
            );
          })
        ) : isLoading || connectionsLoading ? (
          <div
            className={styles.chatSkeletonList}
            aria-label="Loading conversations"
            aria-busy="true"
          >
            {Array.from({ length: 7 }, (_, index) => (
              <div
                className={styles.chatSkeletonRow}
                key={`chat-skeleton-${index}`}
              >
                <span className={styles.skeletonAvatar} />
                <span className={styles.skeletonText}>
                  <span className={styles.skeletonName} />
                  <span className={styles.skeletonSubtitle} />
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "#999",
              fontSize: "15px",
            }}
          >
            {chatsError
              ? "Chats could not be loaded."
              : searchTerm
                ? "No matching results"
                : connectedProfilesOnly
                  ? "No connections in this category"
                  : "No recent chats"}
          </div>
        )}
        {chatsError && (
          <div className={styles.connectionLoadError} role="alert">
            <span>{chatsError}</span>
            <button
              type="button"
              onClick={onRetryChats}
              disabled={!onRetryChats}
            >
              Retry
            </button>
          </div>
        )}
        {activeTabType !== "user" &&
          !isLoading &&
          (connectionsLoading ? (
            <div
              className={styles.connectedSkeletonList}
              aria-label="Loading connected profiles"
              aria-busy="true"
            >
              {Array.from({ length: 3 }, (_, index) => (
                <div
                  className={styles.connectedSkeletonRow}
                  key={`connected-skeleton-${index}`}
                >
                  <span className={styles.connectedSkeletonAvatar} />
                  <span className={styles.connectedSkeletonText}>
                    <span className={styles.connectedSkeletonName} />
                    <span className={styles.connectedSkeletonSubtitle} />
                  </span>
                </div>
              ))}
            </div>
          ) : connectionsError ? (
            <div className={styles.connectionLoadError} role="alert">
              <span>{connectionsError}</span>
              <button
                type="button"
                onClick={onRetryConnections}
                disabled={!onRetryConnections}
              >
                Retry
              </button>
            </div>
          ) : null)}
      </div>
    </div>
  );
};

export { LeftSection };
export default LeftSection;
